import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@mui/material';
import { Form, FormTextInput } from 'application/Shared/Components/Form';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ChooseGroups } from '../Steps/ChooseGroups';
import { excludeGroups } from './functions';
import { manualIntegrationFormSchema } from './validator';

const Hr = () => (
    <Box mx="24px" mb="24px">
        <hr />
    </Box>
);

const FormFields = ({ tenantSyncConfigId, tenantToEditIntegration }) => {
    const { t } = useTranslation();
    const { setValue } = useFormContext();

    const tenantToEditIntegrationDomains = tenantToEditIntegration?.domains;
    const tenantToEditIntegrationName = tenantToEditIntegration?.name;

    useEffect(() => {
        if (tenantToEditIntegrationName) {
            setValue('name', tenantToEditIntegrationName);
        }
    }, [tenantToEditIntegrationName, setValue]);

    useEffect(() => {
        if (tenantToEditIntegrationDomains) {
            setValue('updateDomains', true);
            setValue('domains', tenantToEditIntegrationDomains.join());
        }
    }, [tenantToEditIntegrationDomains, setValue]);

    return (
        <Box mx="24px" mb="24px">
            <FormTextInput
                inputProps={{
                    readOnly:
                        !!tenantSyncConfigId || tenantToEditIntegrationName
                }}
                name="name"
                label={t('Common.client_name')}
                placeholder={t('Common.brand_name')}
                required
            />
            <FormTextInput
                name="domains"
                label={t('Clients.domain(s)')}
                placeholder="saas.com,saas.onmicrosoft.com"
                tooltipText={t('Tooltips.domains_comma_separated')}
                required
            />
            <FormTextInput
                name="azureTenantId"
                label={t('Provider.MicrosoftConfiguration.tenant_id')}
                required
            />
            <FormTextInput
                name="azureApplicationId"
                label={t('Provider.MicrosoftConfiguration.application_id')}
                required
            />
            <FormTextInput
                name="azureApplicationSecret"
                label={t('Provider.MicrosoftConfiguration.application_secret')}
                type="password"
                required
            />
        </Box>
    );
};

const ChooseGroupsForm = ({
    clientId,
    onboarding,
    groups,
    setApplicationId,
    setApplicationSecret,
    setGroups,
    setIdentityTenantId,
    useIntegrationWizard
}) => {
    const { watch } = useFormContext();
    const [applicationId, applicationSecret, identityTenantId] = watch([
        'azureApplicationId',
        'azureApplicationSecret',
        'azureTenantId'
    ]);

    useEffect(() => {
        setApplicationId(applicationId);
    }, [applicationId, setApplicationId]);

    useEffect(() => {
        setApplicationSecret(applicationSecret);
    }, [applicationSecret, setApplicationSecret]);

    useEffect(() => {
        setIdentityTenantId(identityTenantId);
    }, [identityTenantId, setIdentityTenantId]);

    return (
        !useIntegrationWizard &&
        !onboarding &&
        applicationId &&
        applicationSecret &&
        identityTenantId && (
            <>
                <Hr />
                <ChooseGroups
                    applicationId={applicationId}
                    applicationSecret={applicationSecret}
                    groups={groups}
                    integrationType="microsoftEntraIDManual"
                    identityTenantId={identityTenantId}
                    linkedTenantId={clientId}
                    providerRoute="azure"
                    setGroups={setGroups}
                />
            </>
        )
    );
};

const defaultValues = {
    azureApplicationId: '',
    azureApplicationSecret: '',
    azureTenantId: '',
    domains: '',
    enableAzure: true,
    name: '',
    providerType: 'Microsoft',
    updateDomains: null,
    useAsmAppCredentials: false
};

export const MicrosoftManualIntegrationForm = ({
    children,
    clientId,
    onboarding,
    onSubmit,
    tenantSyncConfigId,
    tenantToEditIntegration,
    useIntegrationWizard
}) => {
    const { t } = useTranslation();

    const [applicationId, setApplicationId] = useState(null);
    const [applicationSecret, setApplicationSecret] = useState(null);
    const [identityTenantId, setIdentityTenantId] = useState(null);

    const [groups, setGroups] = useState({});

    const providerRoute = 'azure';

    const onDone = async (values) => {
        const providerInfo = {
            applicationId,
            applicationSecret,
            identityTenantId
        };
        const success = await excludeGroups({
            clientId,
            groups,
            onboarding,
            providerInfo,
            providerRoute,
            t,
            useIntegrationWizard
        });
        if (success) {
            await onSubmit(values);
        }
    };

    return (
        <Box>
            <Form
                defaultValues={defaultValues}
                validator={yupResolver(
                    manualIntegrationFormSchema({ t, tenantSyncConfigId })
                )}
                onSubmit={onDone}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                }}
            >
                <FormFields
                    tenantSyncConfigId={tenantSyncConfigId}
                    tenantToEditIntegration={tenantToEditIntegration}
                />
                <ChooseGroupsForm
                    clientId={clientId}
                    groups={groups}
                    onboarding={onboarding}
                    setApplicationId={setApplicationId}
                    setApplicationSecret={setApplicationSecret}
                    setGroups={setGroups}
                    setIdentityTenantId={setIdentityTenantId}
                    useIntegrationWizard={useIntegrationWizard}
                />

                {/* Footer is the children */}
                {children}
            </Form>
        </Box>
    );
};
